/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { MdDelete, MdAdd, MdList } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { formatDistance } from 'date-fns';

import { Button, DialogContentText } from '@material-ui/core';

import DataTable from '~/components/DataTable';
import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';
import SimpleDialog from '~/components/SimpleDialog';

import history from '~/services/history';
import api from '~/services/pluginbot-api';
import { expireSession } from '~/store/modules/auth/actions';
import getDateLocale from '~/util/GetDateLocale';

export default function ArticlesList() {
    const dispatch = useDispatch();
    const _settings = useSelector(state => state.settings || null);
    const date_loc = getDateLocale(_settings);

    const [isLoading, setIsLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [currItem, setCurrItem] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const tableHeaders = [
        { id: 'title', label: <LocaleMessage msg="table.headers.title" /> },
        {
            id: 'category_name',
            label: <LocaleMessage msg="page.articles.form.label.category" />,
        },
        {
            id: 'status_label',
            label: <LocaleMessage msg="table.headers.status" />,
        },
        {
            id: 'updated',
            label: <LocaleMessage msg="table.headers.updated" />,
            order_by: 'updated_timestamp',
        },
    ];

    function requestError(error) {
        if (error.response) {
            const message = (
                <LocaleMessage msg={`errors.${error.response.data.code}`} />
            );
            toast.error(message);
            const { status } = error.response;
            if (status === 401) {
                dispatch(expireSession());
            }
        } else if (error.request) {
            toast.error(<LocaleMessage msg="errors.request" />);
        } else {
            toast.error(<LocaleMessage msg="errors.unknown" />);
        }
        setIsLoading(false);
    }

    async function loadItems() {
        setIsLoading(true);
        await api
            .get(`admin/articles`)
            .then(response => {
                const data = response.data.map(a => {
                    const updated = new Date(a.updated);
                    return {
                        ...a,
                        category_name: a.category ? a.category.name : '---',
                        status_label: (
                            <LocaleMessage
                                msg={`page.articles.status.${a.status}`}
                            />
                        ),
                        updated: formatDistance(updated, new Date(), {
                            addSuffix: true,
                            locale: date_loc,
                        }),
                        updated_timestamp: updated.toISOString(),
                    };
                });
                setItems(data);
                setTimeout(() => {
                    setIsLoading(false);
                }, 100);
            })
            .catch(error => requestError(error));
    }

    async function deleteItem() {
        setIsLoading(true);
        setDeleteOpen(false);
        await api
            .delete(`admin/articles/${currItem}`)
            .then(async () => {
                toast.success(
                    <LocaleMessage msg="page.articles.list.delete_success" />
                );
                await loadItems();
            })
            .catch(error => requestError(error));
        setIsLoading(false);
    }

    useEffect(() => {
        loadItems();
    }, [date_loc]);

    function handleAddOpen() {
        history.push('/articles/new');
    }

    function handleTableRowClick(event, id) {
        event.preventDefault();
        setCurrItem(id);
        history.push(`/articles/${id}`);
    }

    function handleDeleteOpen(event, _id) {
        setCurrItem(_id);
        event.preventDefault();
        setDeleteOpen(true);
    }

    function handleDialogClose(event) {
        event.preventDefault();
        setDeleteOpen(false);
    }

    function buildListView() {
        const rowActions = [
            {
                id: 'delete',
                label: <LocaleMessage msg="button.delete" />,
                icon: <MdDelete />,
                action: handleDeleteOpen,
            },
        ];

        return (
            <div style={{ minHeight: '150px', width: '100%', padding: '15px' }}>
                <DataTable
                    headerColumns={tableHeaders}
                    data={items}
                    orderColumn="title"
                    handleTableRowClick={(event, id) =>
                        handleTableRowClick(event, id)
                    }
                    rowActions={rowActions}
                />
            </div>
        );
    }

    function buildDeleteDialog() {
        return (
            <SimpleDialog
                open={deleteOpen}
                onClose={handleDialogClose}
                title={<LocaleMessage msg="page.articles.list.delete_title" />}
                content={
                    <DialogContentText>
                        <LocaleMessage msg="message.undone.content" />
                    </DialogContentText>
                }
                actions={[
                    {
                        key: 'cancel',
                        onClick: () => setDeleteOpen(false),
                        label: <LocaleMessage msg="button.cancel" />,
                    },
                    {
                        key: 'delete',
                        onClick: () => deleteItem(),
                        label: <LocaleMessage msg="button.delete" />,
                    },
                ]}
            />
        );
    }

    return (
        <PageContent
            title={<LocaleMessage msg="page.articles.title" />}
            breadcrumbs={[
                {
                    url: '/',
                    title: <LocaleMessage msg="breadcrumbs.home" />,
                },
            ]}
            loading={isLoading}
        >
            <>
                {deleteOpen ? buildDeleteDialog() : null}
                <>
                    <div className="body-top-controls">
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<MdAdd />}
                            style={{
                                whiteSpace: 'nowrap',
                                padding: '5px 20px',
                            }}
                            onClick={() => handleAddOpen()}
                        >
                            <LocaleMessage msg="page.articles.list.add" />
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<MdList />}
                            style={{
                                whiteSpace: 'nowrap',
                                padding: '5px 20px',
                                marginLeft: '10px',
                            }}
                            onClick={() => history.push('/articles/categories')}
                        >
                            <LocaleMessage msg="page.articles.categories.title" />
                        </Button>
                    </div>
                    {buildListView()}
                </>
            </>
        </PageContent>
    );
}
